import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Paper,
  AppBar,
  Toolbar,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  Drawer,
  CircularProgress,
  Alert,
  Tooltip,
  Checkbox,
  FormControlLabel,
  Popover,
  FormGroup,
  Button,
  Chip,
  Menu,
  MenuItem,
  ListItemText,
} from '@mui/material';
import {
  Person as PersonIcon,
  Business as BusinessIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Settings as SettingsIcon,
  Info as InfoIcon,
  Close as CloseIcon,
  FilterList as FilterListIcon,
  AccountTree as ClusterIcon,
} from '@mui/icons-material';
import { useAuth } from '../../hooks/useAuth';
import GraphCanvas from '../../components/KnowledgeGraph/GraphCanvas/GraphCanvas';
import GraphQueryBar from '../../components/KnowledgeGraph/GraphQueryBar/GraphQueryBar';
import GraphDetailsPanel from '../../components/KnowledgeGraph/GraphDetailsPanel/GraphDetailsPanel';
import GraphStatsCard from '../../components/KnowledgeGraph/GraphStats/GraphStatsCard';
import GraphToolbar from '../../components/KnowledgeGraph/GraphToolbar/GraphToolbar';
import GraphFilterPanel from '../../components/KnowledgeGraph/GraphFilterPanel/GraphFilterPanel';
import GraphMiniMap from '../../components/KnowledgeGraph/GraphMiniMap/GraphMiniMap';
import TemporalSlider from '../../components/KnowledgeGraph/TemporalSlider/TemporalSlider';
import GraphExportDialog from '../../components/KnowledgeGraph/GraphExportDialog/GraphExportDialog';
import { graphApi } from '../../services/api/graph.service';
import { GraphNode, GraphEdge, GraphScope, GraphStats, GraphFilterOptions } from '../../types/graph.types';
import { ModeSelector } from '../../components/Navigation';
import GraphOnboardingModal from '../../components/KnowledgeGraph/GraphOnboardingModal/GraphOnboardingModal';
import { ClusterStrategy } from '../../utils/graphClustering';
import { useFeatureFlags } from '../../hooks/useFeatureFlags';
import { applyTemporalStyling, clearTemporalStyling } from '../../utils/graphAnimations';
import { Core } from 'cytoscape';

/**
 * Knowledge Graph Visualization Page
 * 
 * This page provides an interactive visualization of the user's knowledge graph.
 * It supports both personal and organization scopes, natural language querying,
 * and detailed exploration of nodes and their relationships.
 * 
 * Future Integration Points:
 * - Todo nodes: When todo system is implemented, todo items will appear as nodes
 * - Task relationships: Tasks can be linked to documents, messages, and people
 * - Due date visualization: Time-based layout for todos with due dates
 */
const KnowledgeGraphPage: React.FC = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const featureFlags = useFeatureFlags();

  // Graph scope (personal vs organization)
  const [scope, setScope] = useState<GraphScope>('personal');
  
  // Graph data
  const [nodes, setNodes] = useState<GraphNode[]>([]);
  const [edges, setEdges] = useState<GraphEdge[]>([]);
  const [stats, setStats] = useState<GraphStats | null>(null);

  // UI state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedNode, setSelectedNode] = useState<GraphNode | null>(null);
  const [detailsPanelOpen, setDetailsPanelOpen] = useState(false);
  const [queryHistory, setQueryHistory] = useState<string[]>([]);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [searchResultNodeIds, setSearchResultNodeIds] = useState<string[]>([]);  // NEW: For search highlighting
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);  // NEW: Advanced filter panel
  const [advancedFilters, setAdvancedFilters] = useState<GraphFilterOptions>({});  // NEW: Advanced filters
  const [clusterStrategy, setClusterStrategy] = useState<ClusterStrategy>(ClusterStrategy.CONVERSATION);
  const [cyInstance, setCyInstance] = useState<Core | null>(null);

  // Temporal view state (Week 7)
  const [temporalMode, setTemporalMode] = useState(false);
  const [temporalStartDate, setTemporalStartDate] = useState<Date>(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)); // 30 days ago
  const [temporalEndDate, setTemporalEndDate] = useState<Date>(new Date());
  const [temporalGranularity, setTemporalGranularity] = useState<'day' | 'week' | 'month'>('day');
  const [temporalDistribution, setTemporalDistribution] = useState<Record<string, number>>({});
  const [temporalMinDate, setTemporalMinDate] = useState<Date>(new Date(Date.now() - 365 * 24 * 60 * 60 * 1000)); // 1 year ago
  const [temporalMaxDate, setTemporalMaxDate] = useState<Date>(new Date());

  // Annotation counts for visual indicators (Week 8)
  const [annotationCounts, setAnnotationCounts] = useState<Record<string, number>>({});

  // Export dialog state (Week 8)
  const [exportDialogOpen, setExportDialogOpen] = useState(false);

  // Node type filtering - show what actually exists in the database
  // Note: Topics are automatically included as connectors and are not user-filterable
  const [nodeTypeFilters, setNodeTypeFilters] = useState<Record<string, boolean>>({
    email: false,    // Emails exist but hidden by default (too many)
    person: false,   // People hidden by default
    message: true,   // Messages shown by default (includes conversations)
    document: true,  // Documents shown by default
    concept: false,  // Concept type not defined in schema yet
    organization: false, // Organizations don't exist yet
  });
  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLButtonElement | null>(null);
  const filterPopoverOpen = Boolean(filterAnchorEl);
  const [clusterAnchorEl, setClusterAnchorEl] = useState<HTMLButtonElement | null>(null);
  const clusterMenuOpen = Boolean(clusterAnchorEl);
  
  // Load initial graph data
  const loadGraphData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Build filter options from node type filters
      // Backend expects capitalized type names (e.g., 'Email' not 'email')
      const enabledTypes = Object.entries(nodeTypeFilters)
        .filter(([_, enabled]) => enabled)
        .map(([type, _]) => {
          // Capitalize first letter to match database schema
          return type.charAt(0).toUpperCase() + type.slice(1);
        });

      // Automatically include Conversation when Message is enabled
      // (Messages belong to conversations, so we need both to show the hierarchy)
      if (enabledTypes.includes('Message') && !enabledTypes.includes('Conversation')) {
        enabledTypes.push('Conversation');
      }

      // Combine node type filters with advanced filters
      const filters: GraphFilterOptions = {
        showNodeTypes: new Set(enabledTypes),
        ...advancedFilters,  // Include date range, search term, min degree
      };

      console.log('🔍 Loading graph with filters:', {
        enabledTypes,
        filterSet: filters.showNodeTypes ? Array.from(filters.showNodeTypes) : [],
        scope
      });

      // Fetch synchronized nodes and edges with backend filtering
      const [syncResponse, statsResponse] = await Promise.all([
        graphApi.getSynchronizedData(scope, filters),
        graphApi.getStats(scope),
      ]);

      // Handle synchronized response - nodes and edges are guaranteed to match
      const nodesData = syncResponse?.data?.nodes || [];
      const edgesData = syncResponse?.data?.edges || [];
      const statsData = statsResponse?.data || statsResponse || null;

      // DEBUG: Log the actual response structure
      const nodesByType = nodesData.reduce((acc: any, n: any) => {
        acc[n.type] = (acc[n.type] || 0) + 1;
        return acc;
      }, {});

      console.log('🔍 API RESPONSE DEBUG:', {
        nodeCount: nodesData.length,
        edgeCount: edgesData.length,
        nodesByType,
        sampleNodes: nodesData.slice(0, 5).map((n: any) => ({
          id: n.id,
          type: n.type,
          label: n.label ? n.label.substring(0, 30) : 'no label',
          hasPropertiesUid: !!n.properties?.uid,
          propertiesUid: n.properties?.uid
        })),
        sampleEdges: edgesData.slice(0, 5).map((e: any) => ({
          id: e.id,
          source: e.source,
          target: e.target,
          type: e.type
        }))
      });

      setNodes(Array.isArray(nodesData) ? nodesData : []);
      setEdges(Array.isArray(edgesData) ? edgesData : []);
      setStats(statsData);

      // Clear search highlights when loading full graph
      setSearchResultNodeIds([]);
    } catch (err) {
      console.error('Failed to load graph data:', err);
      setError('Failed to load knowledge graph. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [scope, nodeTypeFilters, advancedFilters]);
  
  // Load graph on mount and scope change
  useEffect(() => {
    loadGraphData();
  }, [loadGraphData]);

  // Check if user is visiting graph for the first time
  useEffect(() => {
    const hasSeenOnboarding = localStorage.getItem('graph_onboarding_seen');
    if (!hasSeenOnboarding) {
      setShowOnboarding(true);
    }
  }, []);

  // Handle scope toggle
  const handleScopeChange = (_: React.MouseEvent<HTMLElement>, newScope: GraphScope | null) => {
    if (newScope !== null) {
      setScope(newScope);
    }
  };
  
  // Handle natural language query
  const handleQuery = useCallback(async (query: string) => {
    setLoading(true);
    setError(null);

    try {
      const response = await graphApi.executeQuery(query, scope);

      // Update graph with query results
      setNodes(response.data.nodes);
      setEdges(response.data.edges);

      // Extract and set matched node IDs for highlighting
      const matchedIds = response.data.matchedNodeIds || [];
      setSearchResultNodeIds(matchedIds);

      console.log('🔍 GRAPH PAGE: Query results received', {
        nodeCount: response.data.nodes.length,
        edgeCount: response.data.edges.length,
        matchedNodeIds: matchedIds,
        highlightingEnabled: matchedIds.length > 0
      });

      // Add to query history
      setQueryHistory(prev => [query, ...prev.slice(0, 9)]);
    } catch (err) {
      console.error('Query execution failed:', err);
      setError('Failed to execute query. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [scope]);
  
  // Handle node selection
  const handleNodeSelect = useCallback((node: GraphNode) => {
    setSelectedNode(node);
    setDetailsPanelOpen(true);
  }, []);
  
  // Handle graph export
  const handleExport = () => {
    setExportDialogOpen(true);
  };
  
  // Handle refresh
  const handleRefresh = () => {
    loadGraphData();
  };

  // Handle filter popover (kept for node type quick filters)
  const handleFilterClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  // Handle advanced filter panel toggle
  const handleAdvancedFilterToggle = () => {
    setFilterPanelOpen(!filterPanelOpen);
  };

  // Handle clustering menu
  const handleClusterMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setClusterAnchorEl(event.currentTarget);
  };

  const handleClusterMenuClose = () => {
    setClusterAnchorEl(null);
  };

  const handleClusterStrategyChange = (strategy: ClusterStrategy) => {
    setClusterStrategy(strategy);
    handleClusterMenuClose();
    console.log('🔗 GRAPH PAGE: Clustering strategy changed', strategy);
  };

  // Handle node type filter toggle
  const handleNodeTypeToggle = (nodeType: string) => {
    setNodeTypeFilters(prev => {
      const updated = {
        ...prev,
        [nodeType]: !prev[nodeType]
      };
      // Backend filtering will automatically trigger via loadGraphData useCallback dependency
      return updated;
    });
  };

  // Get count of active filters
  const activeFilterCount = Object.values(nodeTypeFilters).filter(Boolean).length;

  // Handle onboarding modal close
  const handleOnboardingClose = () => {
    setShowOnboarding(false);
  };

  // Handle "don't show again"
  const handleOnboardingDontShowAgain = () => {
    localStorage.setItem('graph_onboarding_seen', 'true');
    setShowOnboarding(false);
  };

  // Handle temporal mode toggle
  const handleTemporalToggle = () => {
    const newMode = !temporalMode;
    setTemporalMode(newMode);

    if (newMode) {
      // Entering temporal mode: load temporal data
      loadTemporalData(temporalStartDate, temporalEndDate, temporalGranularity);
    } else {
      // Exiting temporal mode: reload full graph and clear temporal styling
      if (cyInstance) {
        clearTemporalStyling(cyInstance);
      }
      loadGraphData();
    }
  };

  // Load temporal graph data
  const loadTemporalData = useCallback(async (start: Date, end: Date, gran: 'day' | 'week' | 'month') => {
    setLoading(true);
    try {
      const response = await graphApi.getTemporalData(scope, start.toISOString(), end.toISOString(), gran);
      const { nodes: temporalNodes, edges: temporalEdges, temporalMetadata } = response.data;

      setNodes(temporalNodes);
      setEdges(temporalEdges);
      setTemporalDistribution(temporalMetadata.distribution);

      if (temporalMetadata.oldestDate) {
        setTemporalMinDate(new Date(temporalMetadata.oldestDate));
      }
      if (temporalMetadata.newestDate) {
        setTemporalMaxDate(new Date(temporalMetadata.newestDate));
      }

      // Apply temporal styling to show age
      if (cyInstance) {
        setTimeout(() => {
          if (cyInstance) {
            applyTemporalStyling(cyInstance, start, end);
          }
        }, 1500); // Wait for layout to complete
      }

      setSearchResultNodeIds([]);
    } catch (err) {
      console.error('Failed to load temporal data:', err);
      setError('Failed to load temporal graph data.');
    } finally {
      setLoading(false);
    }
  }, [scope, cyInstance]);

  // Handle temporal range change from slider
  const handleTemporalRangeChange = useCallback((start: Date, end: Date) => {
    setTemporalStartDate(start);
    setTemporalEndDate(end);
    loadTemporalData(start, end, temporalGranularity);
  }, [loadTemporalData, temporalGranularity]);

  // Handle granularity change
  const handleTemporalGranularityChange = useCallback((gran: 'day' | 'week' | 'month') => {
    setTemporalGranularity(gran);
    if (temporalMode) {
      loadTemporalData(temporalStartDate, temporalEndDate, gran);
    }
  }, [temporalMode, temporalStartDate, temporalEndDate, loadTemporalData]);

  // Fetch annotation counts when nodes change (Week 8)
  useEffect(() => {
    if (!featureFlags.graphAnnotations || nodes.length === 0) return;

    const nodeIds = nodes.map(n => n.id).slice(0, 200); // Limit to 200 nodes for performance
    graphApi.getAnnotationCounts(nodeIds, scope)
      .then(response => {
        setAnnotationCounts(response.data || {});
      })
      .catch(() => {
        // Silently fail - annotations are non-critical
      });
  }, [nodes, scope, featureFlags.graphAnnotations]);

  // Handle advanced filter apply
  const handleApplyAdvancedFilters = (filters: GraphFilterOptions) => {
    setAdvancedFilters(filters);
    console.log('📊 GRAPH PAGE: Advanced filters applied', filters);
  };

  // Handle clear advanced filters
  const handleClearAdvancedFilters = () => {
    setAdvancedFilters({});
    console.log('📊 GRAPH PAGE: Advanced filters cleared');
  };

  // Handle nodeId query parameter for direct node viewing
  useEffect(() => {
    const nodeId = searchParams.get('nodeId');
    if (nodeId && nodes.length > 0) {
      // Find the node with the specified ID (handles both 0x format and regular IDs)
      const targetNode = nodes.find(node =>
        node.id === nodeId ||
        node.id.toLowerCase() === nodeId.toLowerCase() ||
        (node.metadata && node.metadata.uid === nodeId) ||
        (node.metadata && node.metadata.nodeId === nodeId)
      );

      if (targetNode) {
        // Select the node and open details panel
        handleNodeSelect(targetNode);

        // Optional: Focus the graph canvas on this node
        // This would require adding a method to GraphCanvas component
        console.log('Focusing on node:', targetNode);
      } else {
        // If node not found in current view, try querying for it
        console.log('Node not found in current view, querying for:', nodeId);
        handleQuery(`uid:${nodeId} OR id:${nodeId}`);
      }
    }
  }, [searchParams, nodes, handleQuery, handleNodeSelect]);

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Top App Bar */}
      <AppBar position="static" elevation={1}>
        <Toolbar>
          <Typography variant="h6" style={{ flexGrow: 0, marginRight: 24 }}>
            Prismatic Forge
          </Typography>

          {/* Mode Selector - Easy switch back to Chat */}
          <div style={{ marginRight: 'auto' }}>
            <ModeSelector size="small" showLabels={true} />
          </div>

          {/* Scope Toggle */}
          <ToggleButtonGroup
            value={scope}
            exclusive
            onChange={handleScopeChange}
            size="small"
            style={{ marginRight: 'auto' }}
          >
            <ToggleButton value="personal">
              <PersonIcon style={{ marginRight: 8 }} />
              Personal
            </ToggleButton>
            <ToggleButton 
              value="organization" 
              disabled={!user?.organizationId}
            >
              <BusinessIcon style={{ marginRight: 8 }} />
              Organization
            </ToggleButton>
          </ToggleButtonGroup>
          
          {/* Stats Display */}
          {stats && (
            <div style={{ marginRight: 16 }}><GraphStatsCard stats={stats} /></div>
          )}
          
          {/* Action Buttons */}
          <Tooltip title="Node Type Filters">
            <IconButton onClick={handleFilterClick} color="inherit">
              <FilterListIcon />
              {activeFilterCount < Object.keys(nodeTypeFilters).length && (
                <span
                  style={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    backgroundColor: '#d32f2f',
                    fontSize: '0.625rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                />
              )}
            </IconButton>
          </Tooltip>

          <Tooltip title="Advanced Filters">
            <Button
              size="small"
              variant={filterPanelOpen ? 'contained' : 'outlined'}
              onClick={handleAdvancedFilterToggle}
              style={{ color: 'inherit', borderColor: 'inherit' }}
              startIcon={<FilterListIcon />}
            >
              {Object.keys(advancedFilters).filter(k => advancedFilters[k as keyof GraphFilterOptions] !== undefined).length > 0 && (
                <Chip
                  label={Object.keys(advancedFilters).filter(k => advancedFilters[k as keyof GraphFilterOptions] !== undefined).length}
                  size="small"
                  style={{ marginLeft: 4, height: 16, fontSize: '0.625rem' }}
                />
              )}
            </Button>
          </Tooltip>

          {featureFlags.temporalGraphView && (
            <Tooltip title="Timeline View">
              <Button
                size="small"
                variant={temporalMode ? 'contained' : 'outlined'}
                onClick={handleTemporalToggle}
                style={{ color: 'inherit', borderColor: 'inherit' }}
              >
                Timeline {temporalMode && 'ON'}
              </Button>
            </Tooltip>
          )}

          <Tooltip title="Clustering">
            <Button
              size="small"
              variant="outlined"
              onClick={handleClusterMenuOpen}
              style={{ color: 'inherit', borderColor: 'inherit' }}
              startIcon={<ClusterIcon />}
            >
              {clusterStrategy !== ClusterStrategy.NONE && 'ON'}
            </Button>
          </Tooltip>

          <Tooltip title="Refresh">
            <IconButton onClick={handleRefresh} color="inherit">
              <RefreshIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Export">
            <IconButton onClick={handleExport} color="inherit">
              <DownloadIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Settings">
            <IconButton color="inherit">
              <SettingsIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Help">
            <IconButton color="inherit">
              <InfoIcon />
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>
      
      {/* Query Bar */}
      <Paper elevation={1} style={{ padding: 16, borderRadius: 0 }}>
        <GraphQueryBar
          onQuery={handleQuery}
          queryHistory={queryHistory}
          disabled={loading}
        />
      </Paper>
      
      {/* Graph Toolbar */}
      <GraphToolbar scope={scope} onExportDialogOpen={() => setExportDialogOpen(true)} />

      {/* Temporal Slider (shown when temporal mode is active) */}
      {temporalMode && featureFlags.temporalGraphView && (
        <TemporalSlider
          minDate={temporalMinDate}
          maxDate={temporalMaxDate}
          startDate={temporalStartDate}
          endDate={temporalEndDate}
          granularity={temporalGranularity}
          distribution={temporalDistribution}
          onChange={handleTemporalRangeChange}
          onGranularityChange={handleTemporalGranularityChange}
        />
      )}

      {/* Main Content Area */}
      <div style={{ flexGrow: 1, position: 'relative', overflow: 'hidden' }}>
        {/* Error Display */}
        {error && (
          <Alert
            severity="error"
            onClose={() => setError(null)}
            style={{ position: 'absolute', top: 16, left: '50%', transform: 'translateX(-50%)', zIndex: 10 }}
          >
            {error}
          </Alert>
        )}

        {/* Loading Overlay */}
        {loading && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(0, 0, 0, 0.1)',
              zIndex: 10,
            }}
          >
            <CircularProgress />
          </div>
        )}
        
        {/* Graph Canvas */}
        <GraphCanvas
          nodes={nodes}
          edges={edges}
          onNodeSelect={handleNodeSelect}
          selectedNode={selectedNode}
          searchResultNodeIds={searchResultNodeIds}
          clusterStrategy={clusterStrategy}
          onCyReady={setCyInstance}
          animationsEnabled={featureFlags.graphAnimations}
          annotationCounts={annotationCounts}
        />

        {/* Mini-Map */}
        <GraphMiniMap cyInstance={cyInstance} />

        {/* Advanced Filter Panel */}
        <GraphFilterPanel
          open={filterPanelOpen}
          onClose={() => setFilterPanelOpen(false)}
          currentFilters={advancedFilters}
          onApplyFilters={handleApplyAdvancedFilters}
          onClearFilters={handleClearAdvancedFilters}
        />
      </div>

      {/* Details Panel Drawer */}
      <Drawer
        anchor="right"
        open={detailsPanelOpen}
        onClose={() => setDetailsPanelOpen(false)}
        PaperProps={{
          sx: {
            width: 400,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }
        }}
      >
        <div style={{ padding: 16, display: 'flex', alignItems: 'center', borderBottom: '1px solid rgba(0,0,0,0.12)', flexShrink: 0 }}>
          <Typography variant="h6" style={{ flexGrow: 1 }}>
            Node Details
          </Typography>
          <IconButton onClick={() => setDetailsPanelOpen(false)}>
            <CloseIcon />
          </IconButton>
        </div>

        {selectedNode && (
          <GraphDetailsPanel
            node={selectedNode}
            edges={edges}
            nodes={nodes}
            scope={scope}
            annotationCount={annotationCounts[selectedNode.id] || 0}
            onRelatedNodeClick={handleNodeSelect}
            onClose={() => setDetailsPanelOpen(false)}
          />
        )}
      </Drawer>

      {/* Node Type Filter Popover */}
      <Popover
        open={filterPopoverOpen}
        anchorEl={filterAnchorEl}
        onClose={handleFilterClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <div style={{ padding: 16, minWidth: 220 }}>
          <Typography variant="subtitle2" style={{ marginBottom: 8, fontWeight: 'bold' }}>
            Filter Node Types
          </Typography>
          <FormGroup>
            {Object.entries(nodeTypeFilters).map(([type, enabled]) => (
              <FormControlLabel
                key={type}
                control={
                  <Checkbox
                    checked={enabled}
                    onChange={() => handleNodeTypeToggle(type)}
                    size="small"
                  />
                }
                label={
                  <Typography variant="body2" style={{ textTransform: 'capitalize' }}>
                    {type}
                  </Typography>
                }
              />
            ))}
          </FormGroup>
          <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
            <Button
              size="small"
              onClick={() => {
                // Select all
                const allEnabled = Object.keys(nodeTypeFilters).reduce(
                  (acc, key) => ({ ...acc, [key]: true }),
                  {}
                );
                setNodeTypeFilters(allEnabled);
              }}
            >
              Select All
            </Button>
            <Button
              size="small"
              onClick={() => {
                // Deselect all
                const allDisabled = Object.keys(nodeTypeFilters).reduce(
                  (acc, key) => ({ ...acc, [key]: false }),
                  {}
                );
                setNodeTypeFilters(allDisabled);
              }}
            >
              Clear All
            </Button>
          </div>
        </div>
      </Popover>

      {/* Clustering Strategy Menu */}
      <Menu
        anchorEl={clusterAnchorEl}
        open={clusterMenuOpen}
        onClose={handleClusterMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem
          selected={clusterStrategy === ClusterStrategy.NONE}
          onClick={() => handleClusterStrategyChange(ClusterStrategy.NONE)}
        >
          <ListItemText
            primary="No Clustering"
            secondary="Show all nodes individually"
          />
        </MenuItem>
        <MenuItem
          selected={clusterStrategy === ClusterStrategy.CONVERSATION}
          onClick={() => handleClusterStrategyChange(ClusterStrategy.CONVERSATION)}
        >
          <ListItemText
            primary="By Conversation"
            secondary="Group messages by conversation"
          />
        </MenuItem>
        <MenuItem
          selected={clusterStrategy === ClusterStrategy.TOPIC}
          onClick={() => handleClusterStrategyChange(ClusterStrategy.TOPIC)}
        >
          <ListItemText
            primary="By Topic"
            secondary="Group nodes by primary topic"
          />
        </MenuItem>
        <MenuItem
          selected={clusterStrategy === ClusterStrategy.TIME_PERIOD}
          onClick={() => handleClusterStrategyChange(ClusterStrategy.TIME_PERIOD)}
        >
          <ListItemText
            primary="By Time Period"
            secondary="Group nodes by day/week/month"
          />
        </MenuItem>
        <MenuItem
          selected={clusterStrategy === ClusterStrategy.NODE_TYPE}
          onClick={() => handleClusterStrategyChange(ClusterStrategy.NODE_TYPE)}
        >
          <ListItemText
            primary="By Node Type"
            secondary="Group nodes of the same type"
          />
        </MenuItem>
      </Menu>

      {/* First-Time User Onboarding Modal */}
      <GraphOnboardingModal
        open={showOnboarding}
        onClose={handleOnboardingClose}
        onDontShowAgain={handleOnboardingDontShowAgain}
      />

      {/* Export Dialog */}
      <GraphExportDialog
        open={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
        scope={scope}
        currentFilters={advancedFilters}
        nodeCount={nodes.length}
        edgeCount={edges.length}
      />
    </div>
  );
};

export default KnowledgeGraphPage;